import { useState, useEffect } from 'react';
import { Loader2, FileText, Sparkles, AlertTriangle, Edit2, Save, X } from 'lucide-react';
import exploreApi from '../../exploreApi';

export default function AIDescriptions({ filePath, sourceId, objectPath, sheetName }) {
  const [descriptions, setDescriptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [usage, setUsage] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editState, setEditState] = useState({});
  const [fileDescEdit, setFileDescEdit] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    const loadSaved = async () => {
      setLoading(true);
      try {
        const resp = await exploreApi.getSavedDescriptions({ file_path: filePath, source_id: sourceId, object_path: objectPath, sheet_name: sheetName });
        if (resp.success && resp.data) {
          setDescriptions(resp.data);
          setFileDescEdit(resp.data.file_description || '');
          setUsage(resp.llm_usage || null);
          const init = {};
          Object.entries(resp.data.column_descriptions || {}).forEach(([k, v]) => { init[k] = { ...v }; });
          setEditState(init);
        }
      } catch (err) {
        console.error('Failed to load saved descriptions:', err);
      } finally {
        setLoading(false);
      }
    };
    loadSaved();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filePath, sourceId, objectPath, sheetName]);

  const generateDescriptions = async () => {
    setGenerating(true);
    setError(null);
    try {
      const resp = await exploreApi.generateDescriptions({ file_path: filePath || '', source_id: sourceId, object_path: objectPath, sheet_name: sheetName });
      if (resp.success && resp.data) {
        setDescriptions(resp.data);
        setFileDescEdit(resp.data.file_description || '');
        setUsage(resp.llm_usage || null);
        const init = {};
        Object.entries(resp.data.column_descriptions || {}).forEach(([k, v]) => { init[k] = { ...v }; });
        setEditState(init);
      } else {
        setError(resp.error || 'Generation failed');
      }
    } catch (err) {
      setError(err.message || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = () => {
    const updated = { ...descriptions, file_description: fileDescEdit, column_descriptions: editState };
    setDescriptions(updated);
    setSaveSuccess(true);
    setEditMode(false);
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-primary mr-3" />
        <span className="text-sm text-gray-500">Loading descriptions…</span>
      </div>
    );
  }

  if (!descriptions && !generating) {
    return (
      <div className="text-center py-12">
        <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-600 font-medium mb-2">No AI descriptions yet</p>
        <p className="text-xs text-gray-400 mb-4">Generate descriptions for each column and the dataset overall</p>
        {error && (
          <div className="mb-4 mx-auto max-w-sm bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2 text-left">
            <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-red-700">{error}</p>
          </div>
        )}
        <button onClick={generateDescriptions} className="px-4 py-2 bg-primary text-white rounded-md text-sm font-medium hover:bg-primaryDark flex items-center gap-2 mx-auto">
          <Sparkles className="h-4 w-4" /> Generate Descriptions
        </button>
      </div>
    );
  }

  if (generating) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Loader2 className="h-10 w-10 animate-spin text-primary mb-4" />
        <p className="text-gray-600 font-medium">Generating AI descriptions…</p>
        <p className="text-xs text-gray-400 mt-1">This may take a minute</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-primary" />
          <span className="font-semibold text-gray-900">AI Descriptions</span>
          {saveSuccess && <span className="text-xs text-green-600">Saved ✓</span>}
        </div>
        <div className="flex gap-2">
          {!editMode ? (
            <>
              <button onClick={() => setEditMode(true)} className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-md text-xs text-gray-600 hover:bg-gray-50">
                <Edit2 className="h-3 w-3" /> Edit
              </button>
              <button onClick={generateDescriptions} className="flex items-center gap-1 px-3 py-1.5 bg-primary text-white rounded-md text-xs hover:bg-primaryDark">
                <Sparkles className="h-3 w-3" /> Regenerate
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setEditMode(false)} className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-md text-xs text-gray-600 hover:bg-gray-50">
                <X className="h-3 w-3" /> Cancel
              </button>
              <button onClick={handleSave} className="flex items-center gap-1 px-3 py-1.5 bg-primary text-white rounded-md text-xs hover:bg-primaryDark">
                <Save className="h-3 w-3" /> Save
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-red-700">{error}</p>
        </div>
      )}

      {/* Dataset description */}
      {(descriptions?.file_description || editMode) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-xs font-semibold text-blue-700 uppercase tracking-wider mb-2">Dataset Description</p>
          {editMode ? (
            <textarea value={fileDescEdit} onChange={e => setFileDescEdit(e.target.value)} className="w-full text-sm text-blue-900 bg-white border border-blue-300 rounded p-2 resize-none" rows={3} />
          ) : (
            <p className="text-sm text-blue-900">{descriptions.file_description}</p>
          )}
        </div>
      )}

      {/* Column descriptions */}
      {descriptions?.column_descriptions && Object.keys(descriptions.column_descriptions).length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <p className="text-sm font-medium text-gray-900">Column Descriptions ({Object.keys(descriptions.column_descriptions).length})</p>
          </div>
          <div className="divide-y divide-gray-100">
            {Object.entries(editMode ? editState : (descriptions.column_descriptions || {})).map(([colName, colData]) => (
              <div key={colName} className="p-4">
                <div className="flex items-start gap-3">
                  <code className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-mono flex-shrink-0">{colName}</code>
                  <div className="flex-1 space-y-2 min-w-0">
                    {editMode ? (
                      <>
                        <textarea
                          value={editState[colName]?.description || ''}
                          onChange={e => setEditState(s => ({ ...s, [colName]: { ...s[colName], description: e.target.value } }))}
                          placeholder="Description"
                          className="w-full text-xs border border-gray-300 rounded px-2 py-1 resize-none"
                          rows={2}
                        />
                        <input
                          value={editState[colName]?.business_meaning || ''}
                          onChange={e => setEditState(s => ({ ...s, [colName]: { ...s[colName], business_meaning: e.target.value } }))}
                          placeholder="Business meaning"
                          className="w-full text-xs border border-gray-300 rounded px-2 py-1"
                        />
                      </>
                    ) : (
                      <>
                        {colData?.description && <p className="text-xs text-gray-700">{colData.description}</p>}
                        {colData?.business_meaning && (
                          <p className="text-xs text-gray-500 italic">Business: {colData.business_meaning}</p>
                        )}
                        {colData?.data_type && (
                          <span className="inline-block px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded text-[10px]">{colData.data_type}</span>
                        )}
                        {colData?.example_values?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {colData.example_values.slice(0, 4).map((v, i) => (
                              <span key={i} className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px] font-mono">{String(v)}</span>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {usage && (
        <p className="text-xs text-gray-400 text-right">
          {usage.model} · {usage.total_tokens?.toLocaleString()} tokens · ${usage.estimated_cost?.toFixed(4)}
        </p>
      )}
    </div>
  );
}
